// 设置：账号（改密码）、API Key、模型、关于。
import { ApiError, request } from './client'
import type { Model } from './types'

export interface Me {
  user: { id: string; username: string; email: string; is_system_admin?: boolean; created_at?: string }
  tenant?: { id: number | string; name: string }
}
export const getMe = () => request<{ data: Me }>('/auth/me').then((r) => r.data)

/** 服务端的英文错误换成中文。 */
const PASSWORD_ERRORS: [RegExp, string][] = [
  [/incorrect|invalid_old_password/i, '现在的密码不对'],
  [/differ|same_password/i, '新密码不能和现在的一样'],
  [/policy/i, '新密码要 8–32 位，并且同时有字母和数字'],
]
export async function changePassword(oldPassword: string, newPassword: string) {
  try {
    await request('/auth/change-password', { body: { old_password: oldPassword, new_password: newPassword } })
  } catch (e) {
    if (e instanceof ApiError) {
      const detail = JSON.stringify(e.data ?? '') + e.message
      const hit = PASSWORD_ERRORS.find(([re]) => re.test(detail))
      if (hit) throw new ApiError(hit[1], e.status)
    }
    throw e
  }
}
/** 和服务端一样的规则：8–32 位，有字母、有数字。 */
export const passwordProblem = (p: string) => (p.length < 8 || p.length > 32 ? '要 8–32 位' : !/[A-Za-z]/.test(p) || !/\d/.test(p) ? '要同时有字母和数字' : null)

export type Capability = 'retrieve' | 'chat' | 'ingest' | 'read_agents' | 'message_history'
export const CAPABILITY_LABEL: Record<string, string> = {
  retrieve: '检索资料',
  chat: '对话',
  ingest: '存资料',
  read_agents: '读智能体',
  message_history: '读对话记录',
  manage_kbs: '管理知识库',
  manage_agents: '管理智能体',
  manage_models: '管理模型',
}
export interface ApiKey {
  id: number
  name: string
  api_key: string
  full_access: boolean
  knowledge_base_ids: string[]
  capabilities: string[]
  last_used_at?: string
  expires_at?: string
  created_at: string
}
const keys = (tenant: string | number) => `/tenants/${encodeURIComponent(String(tenant))}/api-keys`
export const listApiKeys = (tenant: string | number) => request<{ data: ApiKey[] }>(keys(tenant)).then((r) => r.data ?? [])
export const createApiKey = (tenant: string | number, k: { name: string; capabilities: string[]; knowledge_base_ids: string[]; expires_at_unix?: number }) =>
  request<{ data: ApiKey & { token: string } }>(keys(tenant), { body: { ...k, full_access: false } }).then((r) => r.data)
export const revokeApiKey = (tenant: string | number, id: number) => request(`${keys(tenant)}/${id}`, { method: 'DELETE' })
/** 只露出头尾。 */
export const maskKey = (k: string) => (k.length > 12 ? `${k.slice(0, 6)}…${k.slice(-4)}` : '••••')

export const listModels = () => request<{ data: Model[] }>('/models').then((r) => r.data ?? [])

/** 给人看的模型名；两个模型同名时（线上 lingzhi-chat 和 lingzhi-extract 都叫 qwen3.8-flash）带上 id 区分。 */
/** 模型名写给人看：qwen3.8-max → Qwen3.8 Max，deepseek-v4.1-flash → DeepSeek V4.1 Flash，glm-5.3 → GLM-5.3。认不出的原样。 */
export function prettyModel(name: string): string {
  const brand: Record<string, string> = { qwen: 'Qwen', deepseek: 'DeepSeek', kimi: 'Kimi', glm: 'GLM', minimax: 'MiniMax' }
  const m = name.match(/^(qwen|deepseek|kimi|glm|minimax)(.*)$/i)
  if (!m) return name
  const b = brand[m[1].toLowerCase()]
  if (b === 'GLM') return `GLM${m[2]}`
  // qwen3.8-max：版本号紧跟品牌；deepseek-v4-pro：品牌后面隔一个空格
  const words = m[2]
    .replace(/^-/, '')
    .split('-')
    .map((w) => (/^[vk]\d/i.test(w) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
  return `${b}${m[2].startsWith('-') ? ' ' : ''}${words.join(' ')}`
}

export function modelLabel(models: Pick<Model, 'id' | 'name' | 'display_name'>[], m: Pick<Model, 'id' | 'name' | 'display_name'>): string {
  const name = m.display_name || prettyModel(m.name)
  return models.filter((x) => (x.display_name || prettyModel(x.name)) === name).length > 1 ? `${name}（${m.id}）` : name
}

export interface SystemInfo {
  version: string
  edition?: string
  commit_id?: string
  build_time?: string
  go_version?: string
  keyword_index_engine?: string
  vector_store_engine?: string
  graph_database_engine?: string
  db_version?: string
  started_at?: string
  uptime_seconds?: number
}
export const systemInfo = () => request<{ data: SystemInfo }>('/system/info').then((r) => r.data)
